export enum TargetIdType {
  RETAIL_PROFILE_ID_TYPE = 'RETAIL_PROFILE_ID_TYPE',
  RXC_RX_PATIENT_ID_TYPE = 'RXC_RX_PATIENT_ID_TYPE',
  SPL_HB_PATIENT_ID_TYPE = 'SPL_HB_PATIENT_ID_TYPE',
  PBM_QL_PARTICIPANT_ID_TYPE = 'PBM_QL_PARTICIPANT_ID_TYPE',
}

export interface UserInfo {
  statusDescription?: string;
  rxDetails?: { primaryRxTied?: string; dependentRxtied?: string };
  carePassEnrollmentInfo?: {
    carePassEnrollmentStatus?: string;
    carePassEnrolled?: boolean;
  };
  caregiveeDetails?: [];
  h4lUser?: boolean;
  personal?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    emailAddress?: string;
    passwordLastChanged?: string;
  };
  caregiverDetails?: [];
  extraCareDetails?: { extraCareTied?: string; extracareCardNumber?: string };
  statusCode?: string;
}

export interface ExperienceResponse {
  statusCode: string;
  statusDescription: string;
}

export interface CommPrefChannel {
  system: ChannelObjSystem;
  subsystem?: string | null;
  subscriptionStatus: 'Y' | 'N' | 'OUT' | 'IN' | null;
  visible?: boolean | null;
  tcpa?: {
    code: 'D' | 'Y';
    type: string;
  } | null;
}

export interface CommPrefObj {
  preferenceType?:
    | 'H100_PREFERENCES'
    | 'COMMUNICATION_PREFERENCES'
    | 'PHARMACY_PREFERENCE'
    | 'CAREMARK_PREFERENCE'
    | 'SPECIALTY_PREFERENCE';
  contactPoints?: {
    value: string;
    system: 'PHONE' | 'SMS' | 'EMAIL';
    use: string | null;
    rank?: number | null;
    status?: string | null;
    statusCode?: string | null;
    valid?: string | null;
  }[];
  programs?: {
    name: string;
    description?: string | null;
    channels: CommPrefChannel[];
    programID?: string;
  }[];
}

export interface CommPreference {
  preferences: CommPrefObj[];
}

export const ConsentIdTypeMap = {
  RX: TargetIdType.RXC_RX_PATIENT_ID_TYPE,
  MANAGE_RX: TargetIdType.SPL_HB_PATIENT_ID_TYPE,
  VIEW_DEDUCTIBLE_AND_MANAGE_RX: TargetIdType.PBM_QL_PARTICIPANT_ID_TYPE,
};

export interface MemberConsents {
  consents: {
    provisions: [
      {
        type: 'allow' | 'deny' | 'NA';
        actions: [
          {
            key: keyof typeof ConsentIdTypeMap;
          },
        ];
      },
    ];
  }[];
}

export interface GetCommunicationPreferencesResponse {
  data?: {
    profile?: {
      emailAddress?: string;
      rxTied?: string;
      extraCareDetails?: {
        extraCareTied?: 'Y' | 'N';
        extracareAccessLevel?: string;
        ecProfileEmail?: string;
        emailOptFlag?: boolean;
        ecSMSEnrolled?: string;
        digitalReceiptPref?: string;
        cardType?: string;
        extracareCardNumber?: string;
        shortCardNumber?: string;
        directMailOptFlag?: boolean;
      };
    };
    preferences?: CommPreference;
    addresses?: Address[];
    memberConsents?: MemberConsents;
    linkedSSKs?: LinkedSSKs[];
  };
}

export interface GetRxCommunicationPreferencesResponse {
  data?: {
    getCommunicationPreferences?: CommPreference;
  };
}

export enum InStoreReceiptsOption {
  ALWAYS_EMAIL,
  ALWAYS_EMAIL_AND_PRINT,
  LET_ME_CHOOSE,
}

export const inStoreReceiptsOptionValues: { [key in InStoreReceiptsOption]: string } = {
  [InStoreReceiptsOption.ALWAYS_EMAIL]: 'D',
  [InStoreReceiptsOption.ALWAYS_EMAIL_AND_PRINT]: 'B',
  [InStoreReceiptsOption.LET_ME_CHOOSE]: 'P',
};

export interface CommunicationPreferences {
  cvsComEmail?: boolean;
  extraCareEmail?: boolean;
  minuteClinicEmail?: boolean;
  extraCareMail?: boolean;
  inStoreReceipts?: InStoreReceiptsOption;
}

export interface UpdateCommunicationPreferences {
  ecCardType?: string;
  ecNo?: string;
  ecShortNo?: string;
  preferences: CommunicationPreferences;
}

export interface UpdateExtraCareProfile {
  cardType: string;
  cardNumber: string;
  emailPreference?: {
    emailId: string;
    emailoptCd: '0' | '1';
  };
  digitalReceiptPreference?: {
    optCd: 'D' | 'B' | 'P';
  };
  smsPreference?: {
    phoneNumber: string;
    smsOptCd: 'Y' | 'O';
  };
  mailPreference?: {
    optCd: 'I' | 'O';
  };
}
export type ChannelObjSystem = 'EM' | 'SMS' | 'PHONE' | 'EMAIL' | 'SECUREINBOX';

export interface Channel {
  system: ChannelObjSystem;
  subsystem?: 'OLP' | null;
  subscriptionStatus: 'IN' | 'OUT' | 'Y' | 'N';
  visible?: boolean | null;
}

export interface Address {
  line?: [string];
  city?: string;
  state?: string;
  postalCode?: string;
  isDefault?: boolean;
  use: AddressUseEnum;
}

export enum AddressUseEnum {
  BILLING = 'Billing',
  HOME = 'Home',
  OLD = 'Old',
  TEMP = 'Temporary',
  WORK = 'Office',
  SHIPPING = 'Shipping',
  PROVIDER = 'Provider',
}

export enum RxNotificationType {
  PRESCRIPTION_ONLY = 'PRESCRIPTION_ONLY',
  PRESCRIPTION_AND_MARKETING = 'PRESCRIPTION_AND_MARKETING',
  NONE = 'NONE',
}

export interface ContactPoint {
  value: string;
  system?: 'PHONE' | 'SMS' | 'EMAIL';
  use: string | null;
  rank?: number | null;
  status?: string | null;
  statusCode?: string | null;
  valid?: string | null;
}

export interface RxProgram {
  name: string;
  description: string;
  channels: CommPrefChannel[];
}

export interface H100UpdateCommPrefsPayload {
  data: {
    idType: TargetIdType.RETAIL_PROFILE_ID_TYPE;
    preferences: H100Preference[];
  };
}

export interface H100Preference {
  preferenceType: 'H100_PREFERENCES';
  contactPoints: ContactPoint[];
  programs: [];
}

export interface RxPreference {
  preferenceType: 'PHARMACY_PREFERENCE';
  contactPoints: ContactPoint[];
  programs: RxProgram[];
}

export interface RxAlertLabel {
  type: string;
  title: string;
  desc: string;
}

export interface SubscriptionOption {
  key: string;
  label?: string;
  desc: string;
}

export interface CaremarkPref {
  programDesc?: string;
  isOptedIn?: boolean;
}

export interface ECAddressObj {
  city_name: string;
  zip_cd: string;
  st_cd: string;
  addr_pref_seq_nbr: number;
  addr1_txt: string;
  addr_type_cd: string;
}

export interface ECCustomerObj {
  last_name: string;
  first_name: string;
  birth_dt: string;
}

export interface ECEMailObj {
  email_pref_seq_nbr: number;
  email_addr_type_cd: string;
  email_addr_txt: string;
}

export interface ECPhoneNumberObj {
  phone_pfx_nbr: string;
  phone_pref_seq_nbr: number;
  phone_type_cd: string;
  phone_sfx_nbr: string;
  phone_area_cd_nbr: string;
}

export interface ECTableStruct {
  row: Array<unknown>;
  tableName?: string;
}

export interface ECInfo {
  tables: ECTableStruct[];
  xtraCarePrefs: {
    optInEc: string;
    optInEmail: string;
    digitalReceipt: string;
    sms: string;
  };
}

export interface ECUserData {
  data: {
    ecCustProfile: ECInfo;
  };
}

export interface ECAddressObj {
  city_name: string;
  zip_cd: string;
  st_cd: string;
  addr_pref_seq_nbr: number;
  addr1_txt: string;
  addr_type_cd: string;
}

export interface ECCustomerObj {
  last_name: string;
  first_name: string;
  birth_dt: string;
}

export interface ECInfo {
  tables: ECTableStruct[];
}

export type UpdateCommPrefsResponse = ExperienceResponse;

export interface PreferencesProgramObj {
  channels: CommPrefChannel[];
  description?: string;
  name: string;
  programID: string;
}

export interface PreferencesObj {
  contactPoints?: ContactPoint[];
  programs?: PreferencesProgramObj[];
  preferenceType?: string;
}

export interface LinkedSSKs {
  lookupId: string;
  preferencesInfo: {
    statusCode: string;
    statusCodeDescription: string;
    preferences: PreferencesObj[];
  };
}

export interface GetXLoBCommPrefsResponse {
  data: {
    linkedSSKs: LinkedSSKs[];
  };
}

export interface UpdateXLoBCommPrefsPayload {
  data: {
    idType: string;
    preferences: CommPrefObj;
    lookupId: string;
  };
}
